import { Observable, BehaviorSubject } from "rxjs";
import { InternationalisationService } from "../../core/forked/angular-internationalisation";
import { ContentType, ImageOrientation } from "../../core/shared/contentTypes";
import { BonusactionBenefitDataModel, BonusactionDataModel } from "../../core/shared/models";
import Utils from "../../core/shared/utils";
import { ISOTBonusAction, ISOTBonusActionBenefit, ISOTUserBonusPointsView } from "./bonusSystemSOT.facade";
import { SOTBasketProductViewModel } from "./basket.model";

function selectValueByLocale(germanValue: string, englishValue: string): string {
  return InternationalisationService.locale === "de" || Utils.IsNullOrWhiteSpace(englishValue) ? germanValue : englishValue;
}

export class SOTBonusAction implements ISOTBonusAction {

  private _model: BonusactionDataModel;
  private _userBonuspointsView: ISOTUserBonusPointsView;
  private _benefits: ISOTBonusActionBenefit[];

  /** Points in basket */
  private _pointsInBasketSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  /** Available user bonus action points */
  private _availablePointsSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(model: BonusactionDataModel, benefits?: ISOTBonusActionBenefit[]) {
    this._model = model;
    this._benefits = benefits;
  }
  /** Bonus action ID */
  public get ID(): number {
    return this._model.id;
  }

  /** Bonus action name */
  public get name(): string {
    // Only German labels are currently maintained; no localized variant exists yet.
    return selectValueByLocale(this._model.label_de, "");
  }

  /** Total user bonus action points */
  private _totalUserBonusActionPoints: number = 0;

  /** Get total user bonus action points (without basket deduction) */
  public get totalUserBonusActionPoints(): number {
    return this._totalUserBonusActionPoints;
  }

  /** Get available user bonus action points */
  public get userAvailablePoints$(): Observable<number> {
    return this._availablePointsSubject.asObservable();
  }

  /** Update available points when basket changes */
  private updateAvailablePoints(): void {
    const me = this;
    const availablePoints = me._totalUserBonusActionPoints - me._pointsInBasketSubject.getValue();
    me._availablePointsSubject.next(availablePoints);
  }

  /** Load user bonus action points from the stored view */
  public loadUserBonusActionPoints(view: ISOTUserBonusPointsView): void {
    const me = this;
    if (view && view.hasData && view.actions) {
      me._userBonuspointsView = view;
      const bonusAction = me._userBonuspointsView.actions.find(action => action.id === me._model.id);
      me._totalUserBonusActionPoints = bonusAction ? bonusAction.points : 0;
      me.updateAvailablePoints();
    }
  }

  /** Benefits */
  public get benefits(): ReadonlyArray<ISOTBonusActionBenefit> {
    const me = this;
    return me._benefits;
  }

  /** Points in basket */
  public get pointsInBasket$(): Observable<number> {
    const me = this;
    return me._pointsInBasketSubject.asObservable();
  }

  /** update points in basket based on current basket products */
  public updatePointsInBasket(basketProducts: SOTBasketProductViewModel[]): void {
    const me = this;
    const newValue = me.getPointsInBasket(basketProducts);
    if (newValue !== me._pointsInBasketSubject.getValue()) {
      me._pointsInBasketSubject.next(newValue);
      me.updateAvailablePoints();
    }
  }

  /** Get points in basket */
  public getPointsInBasket(basketProducts: SOTBasketProductViewModel[]): number {
    const me = this;
    return basketProducts
      .filter(p => p.isBenefitItem(p.benefitLossId, p.bonusactionId, p.benefitId)
        && p.bonusactionId === me.ID)
      .reduce((sum, p) => sum + (p.benefitCostPoints || 0) * p.count, 0);
  }

  /** Check if benefits in basket cost more points than the user has */
  public isUncovered(basketProducts: SOTBasketProductViewModel[]): boolean {
    const me = this;
    return me.getPointsInBasket(basketProducts) > me._totalUserBonusActionPoints;
  }

  /** Count of given benefit currently in the basket */
  public getCountInBasket(benefit: ISOTBonusActionBenefit, basketProducts: SOTBasketProductViewModel[]): number {
    const me = this;
    return basketProducts
      .filter(p => p.isBenefitItem(p.benefitLossId, p.bonusactionId, p.benefitId)
        && p.bonusactionId === me.ID
        && p.benefitId === benefit.ID)
      .reduce((sum, p) => sum + p.count, 0);
  }

  /** Check if user can redeem count of given benefit */
  public canRedeem(benefit: ISOTBonusActionBenefit, count: number = 1): boolean {
    const me = this;
    return me._availablePointsSubject.getValue() >= benefit.getCostForCount(count);
  }

  /** Progress percentage for given benefit */
  public getProgressPercentage(benefit: ISOTBonusActionBenefit): number {
    const me = this;
    return benefit.getPercentage(me._availablePointsSubject.getValue());
  }

  /** Max number of benefit the user can have in basket given current points */
  public getMaxRedeemable(benefit: ISOTBonusActionBenefit, basketProducts: SOTBasketProductViewModel[]): number {
    const me = this;
    // costPoints === 0 treated as config error & block redemption
    if (benefit.costPoints === 0) return 0;
    // clamp negative available points (uncovered state) so stillAffordable never goes below 0
    const availablePoints = Math.max(0, me._availablePointsSubject.getValue());
    const stillAffordable = Math.floor(availablePoints / benefit.costPoints);
    return stillAffordable + me.getCountInBasket(benefit, basketProducts);
  }

  /**Resets all session bound state (user points, basket points, view) */
  public resetUserState(): void {
    const me = this;
    me._userBonuspointsView = null;
    me._totalUserBonusActionPoints = 0;
    me._pointsInBasketSubject.next(0);
    me._availablePointsSubject.next(0);
  }
}

export class SOTBonusActionBenefit implements ISOTBonusActionBenefit {

  private _model: BonusactionBenefitDataModel;

  constructor(model: BonusactionBenefitDataModel) {
    this._model = model;
  }

  public get ID(): number {
    return this._model.id;
  }
  public get bonusActionID(): number {
    return this._model.bonusaction_id;
  }

  public get productID(): number {
    return this._model.product_id;
  }

  public get lossID(): number {
    return this._model.loss_id;
  }

  public get costPoints(): number {
    return this._model.points;
  }

  public get imageUrl(): string {
    return ContentType.Bonussystem_BenefitImage + "/" + this._model.id + "_photo_" + ImageOrientation.Landscape + "/" + this._model.last_image_update_at_ymd + "/" + this._model.last_image_update_at_hms;
  }

  public get hasImage(): boolean {
    return this._model.last_image_update_at_ymd > 0;
  }

  public get name(): string {
    // Only German labels are currently maintained; no localized variant exists yet.
    return selectValueByLocale(this._model.label_de, "");
  }

  /**
   * Calculate percentage of available points relative to benefit cost
   * @param availablePoints Current available points
   * @returns Percentage value (0-100+)
   */
  public getPercentage(availablePoints: number): number {
    if (this.costPoints === 0) return 0;
    return Math.floor((availablePoints * 100) / this.costPoints);
  }

  /** Total point cost for redeeming this benefit count times */
  public getCostForCount(count: number): number {
    return this.costPoints * count;
  }
}
